import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ArrowRight,
  Zap,
  Shield,
  Wallet,
  MapPin,
  Check,
  Mail,
  Loader2,
  Info,
  Clock,
  CheckCircle2,
  Ruler,
  Users,
  ShoppingBag,
  Quote,
  BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import Marquee from "@/components/Marquee";
import MaterialInfoDialog from "@/components/MaterialInfoDialog";
import SizeGuideDialog from "@/components/SizeGuideDialog";
import PriceComparisonDialog from "@/components/PriceComparisonDialog";
import LatexComparisonDialog from "@/components/LatexComparisonDialog";
import RatingScale from "@/components/RatingScale";
import FAQSection from "@/components/FAQSection";
import GloveAnatomy from "@/components/GloveAnatomy";
import ReviewsSection from "@/components/ReviewsSection";
import StockistsSection from "@/components/StockistsSection";
import TeamPartnersSection from "@/components/TeamPartnersSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => (src && src.startsWith("/") ? `${BACKEND}${src}` : src);
const GREEN_GLOVE = `${BACKEND}/api/static/images/green-mamba.png`;

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) {
    const y = el.getBoundingClientRect().top + window.scrollY - 70;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

const CATEGORIES = ["gloves", "apparel", "accessories"];

const Landing = () => {
  const { t, lang } = useLanguage();
  const cart = useCart();
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState("gloves");
  const [materialOpen, setMaterialOpen] = useState(null); // "german_latex" | "neoprene" | null
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [priceCompOpen, setPriceCompOpen] = useState(false);
  const [latexCompOpen, setLatexCompOpen] = useState(false);
  const [selectedSize, setSelectedSize] = useState({}); // {slug: size}
  const SIZES = ["7", "8", "9", "10", "11"];

  // Contact form
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    // SEO: set German-keyword title for landing
    document.title = lang === "de"
      ? "Torwarthandschuhe Schweiz | 4keeperz — German Latex · Pro Quality"
      : "Goalkeeper Gloves Switzerland | 4keeperz — German Latex · Pro Quality";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        lang === "de"
          ? "Torwarthandschuhe Schweiz — 4keeperz: Premium Torwarthandschuhe aus German Giga Grip Latex und Breathapren Body. Swiss Brand, fairer Preis. Mamba & Panther Edition. Versand CH & FL."
          : "Goalkeeper Gloves Switzerland — 4keeperz: Premium gloves with German Giga Grip latex and Breathapren body. Swiss brand, fair price. Mamba & Panther Edition. Ships CH & FL."
      );
    }
    axios
      .get(`${API}/products`)
      .then((r) => setProducts(r.data))
      .catch((e) => console.error("products err", e));
  }, [lang]);

  const icons = [Zap, Shield, Wallet, MapPin];

  const filteredProducts = useMemo(() => {
    if (activeCategory === "all") return products;
    return products.filter((p) => p.category === activeCategory);
  }, [products, activeCategory]);

  // Group products by category for display
  const productsByCategory = useMemo(() => {
    const groups = { gloves: [], apparel: [], accessories: [] };
    products.forEach((p) => {
      if (groups[p.category]) groups[p.category].push(p);
    });
    return groups;
  }, [products]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(lang === "de" ? "Bitte fülle alle Pflichtfelder aus." : "Please fill all required fields.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const getCategoryBadge = (category) => t.featured.category_labels[category] || category;

  const renderProductCard = (product, i) => {
    if (product.coming_soon) {
      return (
        <article
          key={product.id}
          data-testid={`product-card-${product.slug}`}
          className="relative bg-[#0A0A0A] border border-dashed border-white/15 overflow-hidden flex flex-col items-center justify-center p-10 min-h-[520px] text-center"
        >
          <div className="absolute top-4 left-4 bg-black/70 border border-white/10 font-condensed uppercase text-[10px] tracking-widest text-white/70 px-2.5 py-1">
            {getCategoryBadge(product.category)}
          </div>
          {product.badge && (
            <div className="absolute top-4 right-4 bg-[#00A651] text-black font-condensed uppercase text-xs tracking-widest px-3 py-1">
              {product.badge[lang]}
            </div>
          )}
          <Clock className="w-10 h-10 text-[#00A651] mb-6" />
          <h3 className="font-brush text-3xl tracking-wide text-white leading-tight mb-3">
            {product.name}
          </h3>
          <p className="text-white/55 text-sm leading-relaxed max-w-xs mb-4">
            {product.short_description[lang]}
          </p>
          {product.price_chf > 0 && (
            <div className="mb-5">
              <div className="text-[#00A651] font-display text-2xl leading-none">
                CHF {product.price_chf.toFixed(0)}
              </div>
              <div className="text-[10px] font-condensed uppercase tracking-widest text-white/40 mt-1">
                {lang === "de" ? "Coming Soon" : "Coming soon"}
              </div>
            </div>
          )}
          <button
            onClick={() => scrollToId("contact")}
            data-testid={`product-prelaunch-${product.slug}`}
            className="inline-flex items-center gap-2 border border-[#00A651] hover:bg-[#00A651] hover:text-black text-[#00A651] font-condensed uppercase tracking-widest text-xs px-4 py-2.5 transition-all"
          >
            {t.product_card.prelaunch_cta}
            <ArrowRight className="w-3 h-3" />
          </button>
        </article>
      );
    }
    return (
    <article
      key={product.id}
      data-testid={`product-card-${product.slug}`}
      className="group relative bg-[#0A0A0A] border border-white/10 overflow-hidden hover:border-[#00A651] transition-colors duration-300 flex flex-col"
    >
      {product.badge && (
        <div className="absolute top-4 left-4 z-10 bg-[#00A651] text-black font-condensed uppercase text-xs tracking-widest px-3 py-1">
          {product.badge[lang]}
        </div>
      )}
      <div className="absolute top-4 right-4 z-10 bg-black/70 backdrop-blur-sm border border-white/10 font-condensed uppercase text-[10px] tracking-widest text-white/70 px-2.5 py-1">
        {getCategoryBadge(product.category)}
      </div>

      <div className="relative aspect-[4/5] overflow-hidden bg-[#141414] group/img">
        {product.image ? (
          <>
            <img
              src={resolveImg(product.image)}
              alt={`${product.name} Torwarthandschuhe – ${product.colorway?.[lang] || ""} · 4keeperz Schweiz`.trim()}
              loading="lazy"
              className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
                product.images && product.images.length > 1
                  ? "group-hover/img:opacity-0 group-hover/img:scale-105"
                  : "group-hover:scale-105"
              }`}
            />
            {product.images && product.images.length > 1 && (
              <img
                src={resolveImg(product.images[1])}
                alt={`${product.name} Innenhand / Palm View – ${product.colorway?.[lang] || ""}`.trim()}
                loading="lazy"
            data-testid={`product-back-image-${product.slug}`}
            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover/img:opacity-100 transition-opacity duration-700"
          />
        )}
        {product.images && product.images.length > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm border border-white/10 font-condensed uppercase tracking-widest text-[10px] text-white/70 px-2 py-1 z-10 pointer-events-none">
            Hover für Rückseite
          </div>
        )}
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-[#0f0f0f] to-[#050505]">
            <ShoppingBag className="w-16 h-16 text-[#00A651]/60 stroke-[1.2]" />
            <div className="font-condensed uppercase tracking-widest text-[10px] text-white/30">
              {lang === "de" ? "Produktfoto folgt" : "Product photo coming"}
            </div>
          </div>
        )}
      </div>

      <div className="p-6 flex flex-col gap-4 flex-1">
        <div>
          <h3 className="font-brush text-3xl tracking-wide text-white leading-none">
            {product.name}
          </h3>
          {product.colorway && (
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className="font-condensed uppercase tracking-widest text-white/40">
                {t.featured.colorway_label}:
              </span>
              <span className="font-condensed uppercase tracking-widest text-[#00A651]">
                {product.colorway[lang]}
              </span>
            </div>
          )}
          <p className="text-white/50 text-sm mt-3 leading-relaxed">
            {product.short_description[lang]}
          </p>
        </div>

        <ul className="space-y-1.5 text-xs text-white/60">
          {product.features[lang].map((f, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <Check className="w-3 h-3 mt-0.5 text-[#00A651] shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {product.ratings && (
          <div className="pt-4 border-t border-white/5" data-testid={`product-ratings-${product.slug}`}>
            <div className="font-condensed uppercase tracking-widest text-[10px] text-white/40 mb-2">
              Performance
            </div>
            <RatingScale ratings={product.ratings} compact testIdPrefix={`rating-${product.slug}`} />
          </div>
        )}

        <div className="mt-auto pt-4 border-t border-white/5 flex items-end justify-between gap-3">
          <div>
            <div className="font-condensed uppercase tracking-widest text-[10px] text-white/40">
              {t.product_card.price_label}
            </div>
            <div className="text-[#00A651] font-display text-3xl leading-none mt-1">
              CHF {product.price_chf.toFixed(0)}
            </div>
          </div>
        </div>

        {product.category === "gloves" && (
          <div className="space-y-3 pt-3 border-t border-white/5">
            <div>
              <div className="font-condensed uppercase tracking-widest text-[10px] text-white/40 mb-2 flex items-center justify-between">
                <span>{lang === "de" ? "Größe wählen" : "Pick size"}</span>
                {(() => {
                  const picked = selectedSize[product.slug];
                  const s = product.stock_by_size?.[picked];
                  if (!picked || s === undefined || s > 5) return null;
                  if (s === 0) return <span className="text-red-400 normal-case tracking-normal">Ausverkauft</span>;
                  return (
                    <span className="text-[#00A651] normal-case tracking-normal">
                      {lang === "de" ? `Nur noch ${s} verfügbar` : `Only ${s} left`}
                    </span>
                  );
                })()}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {SIZES.map((sz) => {
                  const stock = product.stock_by_size?.[sz];
                  const outOfStock = stock === 0;
                  const picked = selectedSize[product.slug] === sz;
                  return (
                    <button
                      key={sz}
                      onClick={() => !outOfStock && setSelectedSize((s) => ({ ...s, [product.slug]: sz }))}
                      disabled={outOfStock}
                      data-testid={`size-${product.slug}-${sz}`}
                      title={outOfStock ? "Ausverkauft" : `${stock ?? ""} verfügbar`}
                      className={`w-9 h-9 text-xs font-display border transition-colors relative ${
                        picked
                          ? "bg-[#00A651] text-black border-[#00A651]"
                          : outOfStock
                          ? "border-white/10 text-white/25 cursor-not-allowed line-through"
                          : "border-white/15 text-white/70 hover:border-[#00A651] hover:text-white"
                      }`}
                    >
                      {sz}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const sz = selectedSize[product.slug];
                  if (!sz) {
                    toast.error(lang === "de" ? "Bitte Größe wählen." : "Please pick a size.");
                    return;
                  }
                  cart.addItem(product, sz);
                  toast.success(
                    lang === "de" ? "Zum Warenkorb hinzugefügt" : "Added to cart",
                    {
                      action: {
                        label: lang === "de" ? "Warenkorb öffnen" : "View cart",
                        onClick: () => cart.setOpen(true),
                      },
                    }
                  );
                }}
                data-testid={`add-to-cart-${product.slug}`}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#00A651] hover:bg-[#008A43] text-black font-condensed uppercase tracking-widest text-[11px] px-3 py-2.5 transition-colors leading-tight"
              >
                <ShoppingBag className="w-3.5 h-3.5 shrink-0" />
                <span>{lang === "de" ? "In den Warenkorb" : "Add to cart"}</span>
              </button>
              <button
                onClick={() => setSizeGuideOpen(true)}
                data-testid={`size-guide-btn-${product.slug}`}
                className="inline-flex items-center gap-1.5 text-[11px] font-condensed uppercase tracking-widest text-white/50 hover:text-[#00A651] border border-white/15 hover:border-[#00A651] px-2.5 py-2.5 transition-colors shrink-0"
              >
                <Ruler className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
        {product.category === "accessories" && !product.coming_soon && (
          <div className="pt-3 border-t border-white/5">
            <button
              onClick={() => {
                cart.addItem(product, null);
                toast.success(
                  lang === "de" ? "Zum Warenkorb hinzugefügt" : "Added to cart",
                  {
                    action: {
                      label: lang === "de" ? "Warenkorb öffnen" : "View cart",
                      onClick: () => cart.setOpen(true),
                    },
                  }
                );
              }}
              data-testid={`add-to-cart-${product.slug}`}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#00A651] hover:bg-[#008A43] text-black font-condensed uppercase tracking-widest text-[11px] px-3 py-2.5 transition-colors leading-tight"
            >
              <ShoppingBag className="w-3.5 h-3.5 shrink-0" />
              <span>{lang === "de" ? "In den Warenkorb" : "Add to cart"}</span>
            </button>
          </div>
        )}
      </div>
    </article>
    );
  };

  const renderComingSoonCard = () => (
    <article
      key="coming-soon"
      data-testid="coming-soon-apparel"
      className="relative bg-[#0A0A0A] border border-dashed border-white/15 overflow-hidden flex flex-col items-center justify-center p-10 min-h-[520px] text-center"
    >
      <div className="absolute top-4 left-4 bg-black/70 border border-white/10 font-condensed uppercase text-[10px] tracking-widest text-white/70 px-2.5 py-1">
        {t.featured.category_labels.apparel}
      </div>
      <Clock className="w-10 h-10 text-[#00A651] mb-6" />
      <h3 className="font-brush text-3xl tracking-wide text-white leading-tight mb-3">
        {t.featured.coming_soon_title}
      </h3>
      <p className="text-white/55 text-sm leading-relaxed max-w-xs mb-6">
        {t.featured.coming_soon_text}
      </p>
      <button
        onClick={() => scrollToId("contact")}
        className="inline-flex items-center gap-2 border border-[#00A651] hover:bg-[#00A651] hover:text-black text-[#00A651] font-condensed uppercase tracking-widest text-xs px-4 py-2.5 transition-all"
        data-testid="apparel-prelaunch-cta"
      >
        {t.product_card.prelaunch_cta}
        <ArrowRight className="w-3 h-3" />
      </button>
    </article>
  );

  // Compute grid based on filter
  const displayedCards = useMemo(() => {
    if (activeCategory === "all") {
      return [
        ...productsByCategory.gloves,
        "coming-soon-apparel",
        ...productsByCategory.accessories,
      ];
    }
    if (activeCategory === "apparel") return ["coming-soon-apparel"];
    return productsByCategory[activeCategory] || [];
  }, [activeCategory, productsByCategory]);

  return (
    <div data-testid="landing-page">
      {/* SEO H1 — visually hidden but crawlable for "Torwarthandschuhe Schweiz" ranking */}
      <h1 className="sr-only">
        {lang === "de"
          ? "Torwarthandschuhe Schweiz · 4keeperz Premium Goalkeeper Gloves mit German Giga Grip Latex"
          : "Goalkeeper Gloves Switzerland · 4keeperz Premium with German Giga Grip Latex"}
      </h1>
      {/* HERO */}
      <section className="relative overflow-hidden hero-grid min-h-screen flex items-center noise-overlay">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-black to-[#002410]" />
        <div
          className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #00A651, transparent 70%)" }}
        />

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 pt-32 pb-20 grid lg:grid-cols-12 gap-8 items-center w-full">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-3 border border-white/15 px-4 py-2 mb-8">
              <span className="w-1.5 h-1.5 bg-[#00A651] animate-pulse" />
              <span className="font-condensed uppercase tracking-[0.3em] text-xs text-white/70">
                {t.hero.kicker}
              </span>
            </div>

            <h2
              data-testid="hero-title"
              className="font-brush text-[12vw] sm:text-[10vw] lg:text-[7vw] xl:text-[6.5rem] leading-[0.9] tracking-tight"
            >
              <span className="block text-white">{t.hero.title_1}</span>
              <span className="block text-white">{t.hero.title_2}</span>
              <span className="block text-[#00A651]">{t.hero.title_3}</span>
            </h2>

            <p className="mt-8 text-lg text-white/70 max-w-xl leading-relaxed">
              {t.hero.subtitle}
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <button
                onClick={() => scrollToId("products")}
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-3 bg-[#00A651] hover:bg-[#008A43] text-black font-condensed uppercase tracking-[0.2em] px-8 py-4 transition-all hover:-translate-y-0.5"
              >
                {t.hero.cta_primary}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => scrollToId("story")}
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-3 border border-white/25 hover:border-[#00A651] hover:text-[#00A651] text-white font-condensed uppercase tracking-[0.2em] px-8 py-4 transition-colors"
              >
                {t.hero.cta_secondary}
              </button>
            </div>

            <div className="mt-10 flex flex-wrap gap-3 items-center">
              <button
                onClick={() => setLatexCompOpen(true)}
                data-testid="hero-badge-latex"
                className="inline-flex items-center gap-2 border border-white/20 hover:border-[#00A651] hover:text-[#00A651] px-4 py-2 text-xs font-condensed uppercase tracking-widest text-white/70 transition-colors"
              >
                <Info className="w-3 h-3" />
                {t.hero.badges[0]}
              </button>
              <button
                onClick={() => setMaterialOpen("neoprene")}
                data-testid="hero-badge-neoprene"
                className="inline-flex items-center gap-2 border border-white/20 hover:border-[#00A651] hover:text-[#00A651] px-4 py-2 text-xs font-condensed uppercase tracking-widest text-white/70 transition-colors"
              >
                <Info className="w-3 h-3" />
                {t.hero.badges[1]}
              </button>
              <span className="inline-flex items-center px-4 py-2 text-xs font-condensed uppercase tracking-widest text-white/40">
                · {t.hero.badges[2]}
              </span>
            </div>
          </div>

          <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: "150ms" }}>
            <div className="relative aspect-square">
              <div
                className="absolute inset-0 border border-[#00A651]/40"
                style={{ clipPath: "polygon(0 0, 100% 0, 100% 85%, 85% 100%, 0 100%)" }}
              />
              <img
                src={GREEN_GLOVE}
                alt="Mamba Edition – Green Mamba"
                className="relative z-10 w-full h-full object-cover"
                style={{ clipPath: "polygon(0 0, 100% 0, 100% 85%, 85% 100%, 0 100%)" }}
              />
              <div className="absolute bottom-4 right-4 z-20 bg-black/80 border border-[#00A651] px-3 py-2">
                <div className="font-condensed uppercase tracking-widest text-[10px] text-white/60">
                  Signature
                </div>
                <div className="font-brush text-2xl text-[#00A651] leading-none mt-0.5">
                  Mamba Edition
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee
        text={t.marquee}
        className="py-8 border-y border-white/10 font-display text-5xl md:text-7xl text-[#00A651]/70"
      />

      {/* PRODUCTS */}
      <section
        id="products"
        className="relative py-24 md:py-32 mx-auto max-w-[1400px] px-6 md:px-12"
        data-testid="products-section"
      >
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="font-condensed uppercase tracking-[0.3em] text-xs text-[#00A651] mb-3">
              {t.featured.kicker}
            </div>
            <h2 className="font-brush text-5xl md:text-7xl leading-none text-white">
              {t.featured.title}
            </h2>
            <p className="text-white/60 mt-4 text-lg max-w-2xl">{t.featured.subtitle}</p>
          </div>
        </div>

        {/* Category Tabs — Order: Handschuhe, Bekleidung, Zubehör, Alle */}
        <div
          className="flex flex-wrap gap-[1px] bg-white/10 border border-white/10 mb-10 w-fit"
          data-testid="category-tabs"
        >
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              data-testid={`tab-${cat}`}
              className={`px-5 py-3 font-condensed uppercase tracking-widest text-xs transition-colors ${
                activeCategory === cat
                  ? "bg-[#00A651] text-black"
                  : "bg-black text-white/60 hover:text-white"
              }`}
            >
              {t.featured.category_labels[cat]}
            </button>
          ))}
          <button
            onClick={() => setActiveCategory("all")}
            data-testid="tab-all"
            className={`px-5 py-3 font-condensed uppercase tracking-widest text-xs transition-colors ${
              activeCategory === "all"
                ? "bg-[#00A651] text-black"
                : "bg-black text-white/60 hover:text-white"
            }`}
          >
            {t.featured.filter_all}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedCards.map((item) =>
            item === "coming-soon-apparel" ? renderComingSoonCard() : renderProductCard(item)
          )}
        </div>
      </section>

      {/* GLOVE ANATOMY — feature infographic */}
      <GloveAnatomy />

      {/* USPs */}
      <section className="relative py-24 md:py-32 bg-[#0A0A0A] border-y border-white/5" data-testid="usps-section">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <h2 className="font-brush text-4xl md:text-6xl leading-none mb-14">{t.usps.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[1px] bg-white/10">
            {t.usps.items.map((item, i) => {
              const Icon = icons[i];
              const isLatex = i === 0;
              const isNeoprene = i === 1;
              const isPricing = i === 2;
              const clickable = isLatex || isNeoprene || isPricing;
              const onClick = isLatex
                ? () => setLatexCompOpen(true)
                : isNeoprene
                ? () => setMaterialOpen("neoprene")
                : isPricing
                ? () => setPriceCompOpen(true)
                : undefined;
              const hintLabel = isLatex
                ? t.materials.compare_cta
                : isPricing
                ? t.price_comparison.cta
                : t.materials.click_hint;
              return (
                <div
                  key={i}
                  data-testid={`usp-${i}`}
                  onClick={onClick}
                  className={`relative bg-black p-8 transition-colors group ${
                    clickable ? "cursor-pointer hover:bg-[#0A0A0A]" : ""
                  }`}
                >
                  <Icon className="w-8 h-8 text-[#00A651] mb-6 group-hover:scale-110 transition-transform" />
                  <h3 className="font-display text-2xl uppercase tracking-wide mb-3">{item.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.text}</p>
                  {clickable && (
                    <div className="mt-5 inline-flex items-center gap-2 text-[11px] font-condensed uppercase tracking-widest text-[#00A651] group-hover:gap-3 transition-all">
                      <Info className="w-3 h-3" />
                      {hintLabel}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOUNDER / STORY */}
      <section
        id="story"
        className="relative py-24 md:py-32 mx-auto max-w-[1400px] px-6 md:px-12"
        data-testid="story-section"
      >
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative aspect-[3/4] bg-[#0A0A0A] border border-white/10 overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_torwart-gear/artifacts/6ypzc7eq_Portr%C3%A4t.png"
                alt="Segsan Gröschel – FC Wädenswil"
                data-testid="founder-portrait"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="font-brush text-5xl text-[#00A651] leading-none">FC Wädenswil</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="font-condensed uppercase tracking-[0.3em] text-xs text-[#00A651] mb-3">
              {t.founder.kicker}
            </div>
            <h2 className="font-brush text-5xl md:text-7xl leading-[0.9] mb-4 text-white">
              {t.founder.name}
            </h2>
            <p className="font-condensed uppercase tracking-widest text-white/60 mb-8">
              {t.founder.role}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 max-w-lg">
              {t.story.stats.map((s, i) => (
                <div key={i} className="border border-white/10 p-5" data-testid={`stat-${i}`}>
                  <div className="font-display text-4xl text-[#00A651] leading-none">{s.value}</div>
                  <div className="font-condensed uppercase tracking-widest text-[10px] text-white/50 mt-2">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Story text block */}
        <div className="mt-20 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <div className="font-condensed uppercase tracking-[0.3em] text-xs text-[#00A651] mb-3">
              {t.story.kicker}
            </div>
            <h3 className="font-brush text-4xl md:text-5xl leading-tight text-white">
              {t.story.title}
            </h3>
            <div className="mt-4 inline-flex items-center gap-3 border border-[#00A651]/40 px-3 py-1.5">
              <span className="w-1.5 h-1.5 bg-[#00A651] animate-pulse" />
              <span className="font-condensed uppercase tracking-[0.25em] text-[11px] text-[#00A651]">
                {t.story.signature_tag}
              </span>
            </div>
          </div>
          <div className="lg:col-span-8 space-y-5 text-white/75 text-lg leading-relaxed">
            {t.story.paragraphs.map((p, i) => (
              <p key={i} className={i === t.story.paragraphs.length - 1 ? "text-white font-medium" : ""}>
                {p}
              </p>
            ))}
            <div className="pt-4 flex items-center gap-4">
              <div className="h-[1px] w-16 bg-[#00A651]" />
              <span className="font-brush text-2xl text-[#00A651]">— Segsan</span>
            </div>
          </div>
        </div>

        {/* R&D COLLAB — Ivan Bonfardin */}
        <div
          className="mt-24 md:mt-32 relative border border-white/10 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A] to-black overflow-hidden"
          data-testid="collab-ivan-block"
        >
          {/* Corner accent */}
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-[#00A651]/10 blur-3xl pointer-events-none" />
          <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
            <span className="w-1.5 h-1.5 bg-[#00A651] animate-pulse" />
            <span className="font-condensed uppercase tracking-[0.25em] text-[10px] text-[#00A651]">
              {t.collab.kicker}
            </span>
          </div>

          <div className="grid lg:grid-cols-12 gap-0">
            {/* Ivan portrait */}
            <div className="lg:col-span-5 relative">
              <div className="relative aspect-[3/4] lg:aspect-auto lg:h-full min-h-[560px] bg-black overflow-hidden">
                <img
                  src={`${BACKEND}/api/static/images/ivan-bonfardin.png`}
                  alt="Ivan Bonfardin – Ex-Profi Spieler, R&D Partner 4keeperz"
                  data-testid="ivan-portrait"
                  className="absolute inset-0 w-full h-full object-cover object-center"
                  loading="lazy"
                />
                {/* subtle right-edge fade into content panel on desktop */}
                <div className="absolute inset-0 lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-black/70" />
                {/* mobile bottom overlay for name */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent lg:hidden" />
                {/* Verified badge */}
                <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm border border-[#00A651]/40 px-2.5 py-1">
                  <BadgeCheck className="w-3.5 h-3.5 text-[#00A651]" />
                  <span className="font-condensed uppercase tracking-widest text-[9px] text-white/90">
                    {t.collab.badge}
                  </span>
                </div>
                {/* Name overlay bottom (mobile only) */}
                <div className="absolute bottom-6 left-6 right-6 lg:hidden">
                  <div className="font-brush text-4xl text-[#00A651] leading-none">
                    {t.collab.name}
                  </div>
                  <div className="font-condensed uppercase tracking-widest text-[11px] text-white/80 mt-2">
                    {t.collab.role}
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-7 p-8 md:p-12 lg:p-16 flex flex-col justify-center">
              <div className="hidden lg:block">
                <h3
                  className="font-brush text-5xl xl:text-6xl leading-[0.9] text-white"
                  data-testid="collab-ivan-name"
                >
                  {t.collab.name}
                </h3>
                <p className="font-condensed uppercase tracking-widest text-xs text-white/60 mt-3">
                  {t.collab.role}
                </p>
                <p className="font-condensed uppercase tracking-[0.2em] text-[11px] text-[#00A651] mt-1">
                  {t.collab.clubs}
                </p>
              </div>

              <div className="lg:hidden">
                <p className="font-condensed uppercase tracking-[0.2em] text-[11px] text-[#00A651]">
                  {t.collab.clubs}
                </p>
              </div>

              <div className="my-8 h-[1px] w-24 bg-[#00A651]" />

              <h4 className="font-condensed uppercase tracking-wider text-lg md:text-xl text-white leading-snug">
                {t.collab.headline}
              </h4>

              <p className="mt-5 text-white/75 text-base md:text-lg leading-relaxed">
                {t.collab.body}
              </p>

              {/* Quote block */}
              <div className="mt-8 relative pl-6 border-l-2 border-[#00A651]">
                <Quote className="absolute -top-2 -left-3 w-6 h-6 text-[#00A651] bg-black p-1" />
                <p className="font-brush text-2xl md:text-3xl text-white leading-tight">
                  „{t.collab.quote}“
                </p>
                <div className="mt-3 font-condensed uppercase tracking-widest text-[11px] text-white/50">
                  — Ivan Bonfardin
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ReviewsSection />

      <TeamPartnersSection />

      <StockistsSection />

      {/* CTA BANNER */}
      <section className="relative py-16 md:py-20 bg-black border-b border-white/5">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-brush text-4xl md:text-5xl leading-none text-white">
              {t.cta_banner.title}
            </h2>
            <p className="text-white/60 mt-3 max-w-xl">{t.cta_banner.subtitle}</p>
          </div>
          <button
            onClick={() => scrollToId("contact")}
            data-testid="cta-banner-button"
            className="inline-flex items-center gap-3 bg-[#00A651] hover:bg-[#008A43] text-black font-condensed uppercase tracking-[0.2em] px-8 py-4 transition-colors shrink-0"
          >
            {t.cta_banner.button}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <FAQSection />

      {/* CONTACT */}
      <section
        id="contact"
        className="relative py-24 md:py-32 mx-auto max-w-[1400px] px-6 md:px-12"
        data-testid="contact-section"
      >
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="font-condensed uppercase tracking-[0.3em] text-xs text-[#00A651] mb-3">
              {t.contact.kicker}
            </div>
            <h2 className="font-brush text-5xl md:text-7xl leading-none mb-6 text-white">
              {t.contact.title}
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-10">{t.contact.subtitle}</p>

            <div className="space-y-4 border-t border-white/10 pt-8">
              <div className="font-condensed uppercase tracking-widest text-xs text-white/40 mb-4">
                {t.contact.info_title}
              </div>
              <a
                href="mailto:info@4keeperz.ch"
                className="flex items-center gap-4 text-white/80 hover:text-[#00A651] transition-colors group"
                data-testid="contact-email-link"
              >
                <div className="w-10 h-10 border border-white/15 flex items-center justify-center group-hover:border-[#00A651]">
                  <Mail className="w-4 h-4" />
                </div>
                <span className="font-condensed tracking-wider text-lg">info@4keeperz.ch</span>
              </a>
              <div className="flex items-center gap-4 text-white/80">
                <div className="w-10 h-10 border border-white/15 flex items-center justify-center">
                  <MapPin className="w-4 h-4" />
                </div>
                <span className="font-condensed tracking-wider text-lg">{t.contact.location}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-[#0A0A0A] border border-white/10 p-8 md:p-10 space-y-5"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block font-condensed uppercase tracking-widest text-xs text-white/50 mb-2">
                    {t.contact.name} *
                  </label>
                  <input
                    required
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="contact-input-name"
                    className="w-full bg-black border border-white/15 focus:border-[#00A651] outline-none px-4 py-3 text-white transition-colors"
                  />
                </div>
                <div>
                  <label className="block font-condensed uppercase tracking-widest text-xs text-white/50 mb-2">
                    {t.contact.email} *
                  </label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="contact-input-email"
                    className="w-full bg-black border border-white/15 focus:border-[#00A651] outline-none px-4 py-3 text-white transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block font-condensed uppercase tracking-widest text-xs text-white/50 mb-2">
                  {t.contact.subject}
                </label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  data-testid="contact-input-subject"
                  className="w-full bg-black border border-white/15 focus:border-[#00A651] outline-none px-4 py-3 text-white transition-colors"
                />
              </div>

              <div>
                <label className="block font-condensed uppercase tracking-widest text-xs text-white/50 mb-2">
                  {t.contact.message} *
                </label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-input-message"
                  className="w-full bg-black border border-white/15 focus:border-[#00A651] outline-none px-4 py-3 text-white transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit"
                className="inline-flex items-center gap-3 bg-[#00A651] hover:bg-[#008A43] disabled:opacity-50 text-black font-condensed uppercase tracking-[0.2em] px-8 py-4 transition-colors"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t.contact.sending}
                  </>
                ) : (
                  <>
                    {t.contact.send}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Material Info Dialog */}
      <MaterialInfoDialog
        open={materialOpen !== null}
        onOpenChange={(v) => setMaterialOpen(v ? materialOpen : null)}
        material={materialOpen}
      />

      {/* Size Guide Dialog */}
      <SizeGuideDialog open={sizeGuideOpen} onOpenChange={setSizeGuideOpen} />

      {/* Price Comparison Dialog */}
      <PriceComparisonDialog open={priceCompOpen} onOpenChange={setPriceCompOpen} />

      {/* Latex Comparison Dialog */}
      <LatexComparisonDialog open={latexCompOpen} onOpenChange={setLatexCompOpen} />
    </div>
  );
};

export default Landing;
